/**
 * The Automation Center's client (A4 + A5b + D9 + D10 + D11b + S1 + T3): nine
 * reads, one preview, three writes and one test send.
 *
 * TEST-EMAIL T3 ADDS THE TEST SEND: one read (the readiness of a test e-mail)
 * and one named `POST` that sends one marked test e-mail to the organisation's
 * test address. Its body carries an optional scenario and nothing else.
 *
 * D9 ADDED A READ: the activity timeline behind the Protokoll tab.
 *
 * D11b ADDS ONE MORE: the run detail behind a row of the Ausführungen tab. A
 * read, so the write guard's verb list is unchanged.
 *
 * S1 ADDS THE MOVE-IN REVIEW: one read (the review reachable from a move-in
 * run — the tenant's current open case and the stored answers) and one named
 * write, the answer itself (`adopt` or `keep`). Both address the run's own
 * path, `…/runs/{id}/review`; the write sends an answer and the fingerprint of
 * the case it answers, and nothing else.
 *
 * D10 ADDS THE E-MAIL TEMPLATE ENDPOINTS (D4), and with them the first `POST`s:
 * two reads (the template in force with its catalogue, and the version
 * history), one preview that RENDERS AND STORES NOTHING, and one write that
 * appends a template version — a custom template, or `source: "system"` to go
 * back. The preview is a `POST` only because it carries a draft; the server
 * takes no database session for it.
 *
 * EVERY MUTATING VERB IS NAMED. A4 shipped this file with no `POST`, `PATCH`,
 * `PUT` or `DELETE` at all, because a mutation helper sitting here unused would
 * have been the first thing a later slice reached for without the endpoint
 * behind it. A5b brought the toggle's `PATCH`, D10 the template `POST`s.
 * `automationsWriteGuard.test.js` reads this source and fails on `PUT` and
 * `DELETE`, on any method outside that list, and on any write export it does
 * not name.
 *
 * ERRORS ARE `Error` OBJECTS (A5b). Until A5b the three reads threw
 * `parseAdminErrorResponse(res)` directly, which returns a STRING — and every
 * consumer in this repo reads `err.message`, so the server's own sentence was
 * dropped on the floor and the caller fell back to a generic German line. This
 * was the only file in `api/adminData/` doing that; 27 others throw an Error.
 * `adminErrorFrom` is the newer of the two canonical forms and its `message` is
 * byte-identical to what `new Error(await parseAdminErrorResponse(res))`
 * produced, so nothing that reads `.message` changes — it merely starts
 * working. It also carries `status` and the server's `code`, which is what a
 * later slice would need to tell a 409 from a 404 without matching on German
 * prose.
 *
 * THE WHOLE ENVELOPE IS RETURNED, following `invoiceDashboard.js` rather than
 * the older domain modules here. Those normalise a paginated answer down to
 * `data.items` and throw `total` away, which leaves a page showing 50 of 300
 * runs with no way to know it is showing a sixth of them. `total`, `limit` and
 * `offset` all matter to the run history's pager.
 *
 * OPTIONAL PARAMETERS ARE OMITTED, NEVER SENT EMPTY, so the endpoint's own
 * defaults apply. This is load-bearing for `is_test`: the UI's "Alle" means NO
 * filter, and `is_test=` or `is_test=all` would be a 422. `offset` is compared
 * against null/undefined rather than for truthiness, so an explicit page-one
 * `offset: 0` still reaches the wire instead of being dropped as falsy.
 *
 * THIS FILE IS A CLIENT AND NOTHING ELSE. No hook, no state, no derived counts,
 * no German labels. The category counts are folded in the page and the wording
 * lives in `automationLabels`; a helper that started computing either here
 * would be a second engine beside the one the reader actually sees.
 */
import {
  API_BASE_URL,
  adminErrorFrom,
  getApiHeaders,
} from "./core";

const BASE = "/api/admin/automations";

/**
 * Build the run-history query string.
 *
 * `is_test` is written only when it is a real boolean. `undefined` and `null`
 * both mean "no filter" and are the UI's default; `false` is a FILTER (show me
 * production runs) and must survive, which is why this checks the type rather
 * than truthiness.
 */
export function buildAutomationRunsQuery(params = {}) {
  const sp = new URLSearchParams();
  if (typeof params.isTest === "boolean") {
    sp.set("is_test", params.isTest ? "true" : "false");
  }
  if (params.limit !== null && params.limit !== undefined) {
    sp.set("limit", String(params.limit));
  }
  if (params.offset !== null && params.offset !== undefined) {
    sp.set("offset", String(params.offset));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

/** Every registered automation with this organisation's state attached. */
export async function fetchAdminAutomations() {
  const res = await fetch(`${API_BASE_URL}${BASE}`, {
    headers: getApiHeaders(),
  });
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * One automation, with its stored configuration and 30-day statistics.
 *
 * Addressed by key, not by a configuration id — a registered type that nobody
 * has configured has no id, and `requirement_reminder` is exactly that today.
 */
export async function fetchAdminAutomation(key) {
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * Who switched this automation, and when — newest first (D9: Protokoll).
 *
 * THE ENDPOINT TAKES NO PARAMETERS. It answers the newest page under its own
 * cap together with the full `total`, so nothing is sent and nothing is
 * invented: a page that wants "show more" needs a backend change first, not a
 * query string the server would ignore. The envelope is returned whole for the
 * same reason as the run history's — `total` is what tells the reader that a
 * capped page is not the whole story.
 *
 * A known automation with no history answers `200` with an empty list; only an
 * unknown key is a `404`. Administrative activity only: runs live in
 * `fetchAdminAutomationRuns`, and the two are never mixed.
 */
export async function fetchAdminAutomationActivity(key) {
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}/activity`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/** One page of this automation's run history. */
export async function fetchAdminAutomationRuns(key, params = {}) {
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}/runs${buildAutomationRunsQuery(params)}`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * One run in full (D11b): the history row plus the contract, the actor and —
 * on a move-in run that stored one — the address change it froze (D7, D11a).
 *
 * ADDRESSED BY THE AUTOMATION'S KEY AND THE RUN'S ID TOGETHER. The server looks
 * the run up under both, so a move-in run is not reachable through the birthday
 * address; a run of another automation, another organisation's run and no run
 * at all are the same `404`. No query string: the endpoint takes none.
 *
 * Returned whole, like every read here. Whether the answer is really about the
 * run that was asked for is the caller's question (`useAutomationRunDetail`),
 * not this file's.
 */
export async function fetchAdminAutomationRun(key, runId) {
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}/runs/${encodeURIComponent(runId)}`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * The move-in address review reachable from one move-in run (S1).
 *
 * `{ case, resolutions }`, returned whole. `case` is the tenant's CURRENT open
 * case — decided live by the server, never part of the run it was reached from
 * — or `null`; `resolutions` are the answers already STORED for the run's
 * contract. A run of another automation, another organisation's run and no
 * run at all are the same `404`, as for the run detail.
 */
export async function fetchAdminAutomationRunReview(key, runId) {
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}/runs/${encodeURIComponent(runId)}/review`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * Answer the review reachable from this run: `adopt` the object's address or
 * `keep` the existing one (S1) — the only review write.
 *
 * TWO FIELDS IN THE BODY, and that is the contract (`extra="forbid"`): the
 * answer and the `fingerprint` of the case the reader saw. No tenant, no
 * contract, no address — the server decides those again inside the tenant lock.
 * A case that changed or was closed meanwhile is a `409` with a `code`
 * (`review_stale`, `review_not_open`, `action_not_allowed`); support mode is a
 * `403`. The answer is returned whole; nothing is constructed here.
 */
export async function resolveAdminAutomationRunReview(key, runId, { decision, fingerprint }) {
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}/runs/${encodeURIComponent(runId)}/review`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({ decision, fingerprint }),
    }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * Switch one automation on or off for the caller's organisation (A5b).
 *
 * THE WHOLE RESPONSE IS RETURNED AND NOTHING IS CONSTRUCTED HERE. The endpoint
 * answers with the same `AutomationDetailResponse` the detail GET returns —
 * assembled by the server's own read model after the write committed — so the
 * caller never has to guess what changed. Building `{ ...automation, enabled }`
 * locally would be the optimistic answer wearing the server's clothes, and it
 * would be wrong precisely when it matters: two admins toggling at once, where
 * the value that comes back is not necessarily the value that was sent.
 *
 * ONE FIELD IN THE BODY. The endpoint declares `extra="forbid"`, so a stray
 * `organization_id`, `automation_key` or `config` is a 422 rather than a silent
 * drop. Sending exactly `{ enabled }` is not defensive style, it is the
 * contract.
 *
 * NO CAPABILITY CHECK HERE. Whether an automation may be switched at all is the
 * registry's answer, and the endpoint enforces it with a 409. A client-side
 * gate exists too — the UI does not render a switch for a type that has none —
 * but it is a UX decision, not the boundary, and duplicating the rule in this
 * file would be a second place for it to go stale.
 */
export async function updateAdminAutomation(key, { enabled }) {
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify({ enabled }),
    }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

// ── D10: the e-mail template (D4 endpoints) ─────────────────────────────

/**
 * The template error, with the two fields the editor needs on top of the
 * server's sentence and code: `field` (`subject_doc` or `body_doc`) and the
 * `issues` of a `template_invalid` — each a `{ path, code }` that never carries
 * document text. Read from the response body `adminErrorFrom` left untouched
 * (it reads a clone).
 */
async function templateErrorFrom(res) {
  const err = await adminErrorFrom(res);
  try {
    const detail = (await res.json())?.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      if (typeof detail.field === "string") err.field = detail.field;
      if (Array.isArray(detail.issues)) {
        err.issues = detail.issues.map((issue) => ({
          path: issue?.path,
          code: issue?.code,
        }));
      }
    }
  } catch (_) {
    // No JSON body: the sentence and the code are all there is.
  }
  return err;
}

/** The documents of a request body, sent only when given (`extra="forbid"`). */
function documentsOf({ subject_doc, body_doc }) {
  const out = {};
  if (subject_doc !== undefined && subject_doc !== null) out.subject_doc = subject_doc;
  if (body_doc !== undefined && body_doc !== null) out.body_doc = body_doc;
  return out;
}

/**
 * The template in force and everything the template tab works with, in ONE
 * read: `effective_source`, `latest_version`, `persisted_source`, the documents
 * (only for a custom template), the placeholder catalogue with example values,
 * the preview scenarios, `capabilities` and `limits` — returned whole.
 *
 * A data automation answers 409 `template_not_supported`; the page never asks
 * for one, because its template tab exists only for `action_type: "send_email"`.
 */
export async function fetchAdminAutomationTemplate(key) {
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}/template`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await templateErrorFrom(res);
  return res.json();
}

/**
 * The stored versions, newest first and without documents, capped by the
 * server; `{ items, total }` returned whole so a capped page can say so.
 */
export async function fetchAdminAutomationTemplateVersions(key) {
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}/template/versions`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await templateErrorFrom(res);
  return res.json();
}

/**
 * Render a template on the server — the system template (in a scenario, where
 * the automation has several) or a draft — and STORE NOTHING.
 *
 * The rendered subject, HTML and text come back as the server made them; the
 * client renders no template of its own.
 */
export async function previewAdminAutomationTemplate(
  key,
  { source, subject_doc, body_doc, scenario_key }
) {
  const body = { source, ...documentsOf({ subject_doc, body_doc }) };
  if (scenario_key !== undefined && scenario_key !== null) {
    body.scenario_key = scenario_key;
  }
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}/template/preview`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw await templateErrorFrom(res);
  return res.json();
}

/**
 * Append the next template version — the only template write.
 *
 * `source: "custom"` stores the documents; `source: "system"` is "back to the
 * system template", itself a new version, and carries none. Append-only on the
 * server: nothing is edited, nothing deleted.
 *
 * `expected_current_version` IS ALWAYS SENT, `null` included ("there was no
 * version yet"): the server answers a stale value with 409
 * `template_version_conflict` instead of overwriting a colleague's save. The
 * version number of the answer is the server's; the client counts nothing.
 */
export async function createAdminAutomationTemplateVersion(
  key,
  { source, subject_doc, body_doc, expected_current_version }
) {
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}/template/versions`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({
        source,
        ...documentsOf({ subject_doc, body_doc }),
        expected_current_version:
          expected_current_version === undefined ? null : expected_current_version,
      }),
    }
  );
  if (!res.ok) throw await templateErrorFrom(res);
  return res.json();
}

// ── TEST-EMAIL T3: one marked test e-mail (T2 endpoints) ─────────────────

/**
 * Whether this automation can send a test e-mail now — the stored
 * prerequisites, the organisation's test address, the sender, the template
 * that would be sent and today's test-send quota; returned whole.
 *
 * A READ, and only a read: it renders nothing and reserves nothing. `ready`
 * means the STORED prerequisites are met, not that a send will succeed — only
 * the POST checks the scenario, renders the template and asks the provider.
 * Asked for when the reader opens the test-send dialog, never on mount.
 */
export async function fetchAdminAutomationTestEmailReadiness(key) {
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}/test-email`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * Send ONE test e-mail of this automation to the organisation's test address.
 *
 * ONE OPTIONAL FIELD IN THE BODY, and that is the contract (`extra="forbid"`):
 * `scenario_key`, sent only when given. No recipient, no organisation, no
 * subject, no template, no tenant, no invoice — the server takes the address
 * from the organisation's settings, the template from the automation's stored
 * history and the data from its fixed examples.
 *
 * NO RETRY HERE, and none anywhere above it: every attempt the server accepts
 * counts against the organisation's daily test-send limit, and an answer that
 * never arrived may still have been a sent e-mail. The answer — `200` sent,
 * `502` with `provider_rejected`/`provider_indeterminate`, or a refusal — is
 * returned or thrown as the server gave it.
 */
export async function sendAdminAutomationTestEmail(key, { scenario_key } = {}) {
  const body = {};
  if (scenario_key !== undefined && scenario_key !== null) {
    body.scenario_key = scenario_key;
  }
  const res = await fetch(
    `${API_BASE_URL}${BASE}/${encodeURIComponent(key)}/test-email`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}
